import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getServicePageBySlug } from '../../services/api';
import { ServicePage } from '../../types/servicePages';
// PackagesSection is now handled by SectionMapper dynamically
import SectionMapper from '../../components/services/SectionMapper';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { renderCountryFlag } from '../../utils/flagUtils';

const DynamicServicePage: React.FC = () => {
  const { countrySlug } = useParams<{ countrySlug: string }>();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState<ServicePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      if (!countrySlug) {
        setError('Country slug is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getServicePageBySlug(countrySlug, i18n.language as 'en' | 'ar');

        if (response.success && response.page) {
          setPage(response.page);
          setError(null);
        } else {
          setError(response.error || 'Failed to load page');
        }
      } catch (err) {
        setError('Failed to load page');      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [countrySlug, i18n.language]);


  // Helper function to get hero background style
  const getHeroBackgroundStyle = () => {
    if (!page) return {};

    const primaryColor = page.hero_bg_color_primary || '#3B82F6';
    const secondaryColor = page.hero_bg_color_secondary || '#EF4444';
    // Convert shorthand direction to full CSS syntax
    const rawDirection = page.hero_gradient_direction || 'to-r';
    const direction = rawDirection === 'to-r' ? 'to right' :
                     rawDirection === 'to-l' ? 'to left' :
                     rawDirection === 'to-t' ? 'to top' :
                     rawDirection === 'to-b' ? 'to bottom' :
                     rawDirection === 'to-tr' ? 'to top right' :
                     rawDirection === 'to-tl' ? 'to top left' :
                     rawDirection === 'to-br' ? 'to bottom right' :
                     rawDirection === 'to-bl' ? 'to bottom left' :
                     rawDirection;

    let style: any = {
      background: `linear-gradient(${direction}, ${primaryColor}, ${secondaryColor})`
    };

    if (page.hero_image) {
      style.backgroundImage = `linear-gradient(${direction}, ${primaryColor}${Math.round((page.hero_overlay_opacity || 0.8) * 255).toString(16).padStart(2, '0')}, ${secondaryColor}${Math.round((page.hero_overlay_opacity || 0.8) * 255).toString(16).padStart(2, '0')}), url(${page.hero_image})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
      style.backgroundBlendMode = 'overlay';
    }

    return style;
  };

  // Helper function to get animated pattern elements
  const getAnimatedPatterns = () => {
    if (!page?.hero_pattern || page.hero_pattern === 'none') return null;

    const animationSpeed = page.hero_animation_speed === 'slow' ? '3s' : page.hero_animation_speed === 'fast' ? '1s' : '2s';
    const textColor = page.hero_text_color || '#FFFFFF';

    switch (page.hero_pattern) {
      case 'circles':
      case 'animated-circles':
        return (
          <>
            <div
              className="absolute top-20 right-20 w-64 h-64 rounded-full opacity-10 animate-pulse"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed
              }}
            />
            <div
              className="absolute bottom-20 left-20 w-48 h-48 rounded-full opacity-10 animate-pulse"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed,
                animationDelay: '1s'
              }}
            />
          </>
        );

      case 'squares':
      case 'floating-squares':
        return (
          <>
            <div
              className="absolute top-32 left-32 w-16 h-16 opacity-10 animate-bounce"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed
              }}
            />
            <div
              className="absolute bottom-32 right-32 w-20 h-20 opacity-10 animate-bounce"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed,
                animationDelay: '0.5s'
              }}
            />
          </>
        );

      case 'waves':
      case 'gradient-waves':
        return (
          <div
            className="absolute inset-0 opacity-20 animate-pulse"
            style={{
              background: `repeating-linear-gradient(45deg, transparent, transparent 35px, ${textColor} 35px, ${textColor} 70px)`,
              animationDuration: animationSpeed
            }}
          />
        );

      case 'shapes':
      case 'floating-shapes':
        return (
          <>
            <div
              className="absolute top-16 right-16 w-20 h-20 opacity-15 animate-bounce transform rotate-45"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed,
                borderRadius: '20%'
              }}
            />
            <div
              className="absolute top-1/2 left-16 w-16 h-16 opacity-10 animate-pulse"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed,
                animationDelay: '1s',
                borderRadius: '50%'
              }}
            />
            <div
              className="absolute bottom-20 right-1/3 w-12 h-12 opacity-20 animate-bounce"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed,
                animationDelay: '0.5s',
                transform: 'rotate(45deg)'
              }}
            />
            <div
              className="absolute top-1/4 right-1/4 w-8 h-8 opacity-15 animate-pulse"
              style={{
                backgroundColor: textColor,
                animationDuration: animationSpeed,
                animationDelay: '2s',
                borderRadius: '30%'
              }}
            />
          </>
        );

      case 'subtle-dots':
        return (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle, currentColor 0.5px, transparent 0.5px)',
              backgroundSize: '15px 15px',
              color: textColor
            }}
          />
        );

      case 'grid-lines':
        return (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
              backgroundSize: '25px 25px',
              color: textColor
            }}
          />
        );

      case 'diagonal-lines':
        return (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'repeating-linear-gradient(45deg, currentColor, currentColor 1px, transparent 1px, transparent 15px)',
              backgroundSize: '20px 20px',
              color: textColor
            }}
          />
        );

      case 'hexagon-pattern':
        return (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle at 50% 50%, currentColor 1px, transparent 1px), radial-gradient(circle at 25% 75%, currentColor 1px, transparent 1px)',
              backgroundSize: '30px 30px, 30px 30px',
              backgroundPosition: '0 0, 15px 15px',
              color: textColor
            }}
          />
        );

      case 'wave-pattern':
        return (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 10px, currentColor 10px, currentColor 11px)',
              backgroundSize: '100% 20px',
              color: textColor
            }}
          />
        );

      case 'circuit-pattern':
        return (
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(90deg, currentColor 1px, transparent 1px), linear-gradient(currentColor 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              color: textColor
            }}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
          <p className="text-gray-600">{error || 'The requested service page could not be found.'}</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="text-white py-20 relative overflow-hidden"
        style={{
          ...getHeroBackgroundStyle(),
          color: page.hero_text_color || '#FFFFFF'
        }}
      >
        {/* Animated Patterns */}
        <div className="absolute inset-0">
          {getAnimatedPatterns()}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-12 ltr:mr-4 rtl:ml-4 flex items-center justify-center">
                {renderCountryFlag(
                  page.country_code || '',
                  page.country_flag || '🏳️',
                  page.country_name || 'Country',
                  'w-16 h-12 object-cover rounded-sm shadow-md'
                )}
              </div>
              <div>
                <h1 className="text-4xl md:text-6xl font-bold mb-2">
                  {page.hero_title || `Company Formation in ${page.country_name || 'Country'}`}
                </h1>
                <p className="text-2xl opacity-80">{page.hero_subtitle || 'Professional Business Setup Services'}</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl opacity-80 max-w-4xl mx-auto mb-8">
              {page.hero_description || 'Start your business journey with our comprehensive company formation services.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {page.hero_cta_text && (
                <motion.a
                  href={page.hero_cta_link || "#packages"}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors duration-200 shadow-xl"
                  style={{ color: page.hero_bg_color_primary || '#3B82F6' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {page.hero_cta_text}
                </motion.a>
              )}
              <motion.a
                href="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200"
                style={{
                  borderColor: page.hero_text_color || '#FFFFFF',
                  color: page.hero_text_color || '#FFFFFF'
                }}
                whileHover={{
                  scale: 1.05,
                  backgroundColor: page.hero_text_color || '#FFFFFF',
                  color: page.hero_bg_color_primary || '#3B82F6'
                }}
                whileTap={{ scale: 0.95 }}
              >
                Free Consultation
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Dynamic Sections */}
      {page.sections && (
        <SectionMapper
          sections={page.sections}
          countryName={page.country_name}
          heroStyle={getHeroBackgroundStyle()}
          countryCode={page.country_code}
          heroColors={{
            primary: page.hero_bg_color_primary || '#3B82F6',
            secondary: page.hero_bg_color_secondary || '#EF4444'
          }}
        />
      )}
    </div>
  );
};

export default DynamicServicePage;